import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = {
    id: "livanespor",
    name: "Livanespor",
    short_name: "Livanespor",
    description: "",
    logo_url: "",
    favicon_url: "",
    primary_color: "#f5dc4c",
    secondary_color: "#ffffff",
    accent_color: "#0b0b0b",
    bg_color: "#0b0b0b",
    font_family: "inherit",
    theme_template: "classic",
    instagram_handle: "",
    website_url: "",
    subscription_plan: "starter",
    plan_display_name: "Starter",
    site_enabled: true,
    features: {},
    is_platform: false,
};

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(DEFAULT_BRANDING);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        // Propagate ?_club= override from URL to backend (dev/preview helper)
        const params = new URLSearchParams(window.location.search);
        const clubOverride = params.get("_club");
        const url = clubOverride ? `/public/club/branding?_club=${encodeURIComponent(clubOverride)}` : "/public/club/branding";
        api.get(url)
            .then((r) => { if (mounted) setBranding({ ...DEFAULT_BRANDING, ...r.data }); })
            .catch(() => {})
            .finally(() => mounted && setLoaded(true));
        return () => { mounted = false; };
    }, []);

    // Apply CSS variables and document title/favicon at runtime
    useEffect(() => {
        if (!branding) return;
        const root = document.documentElement;
        root.style.setProperty("--brand-primary", branding.primary_color || "#f5dc4c");
        root.style.setProperty("--brand-secondary", branding.secondary_color || "#ffffff");
        root.style.setProperty("--brand-accent", branding.accent_color || "#0b0b0b");
        root.style.setProperty("--brand-bg", branding.bg_color || "#0b0b0b");
        root.style.setProperty("--liv-yellow", branding.primary_color || "#f5dc4c");
        root.style.setProperty("--liv-yellow-hover", shadeColor(branding.primary_color || "#f5dc4c", -10));

        // Only set document.title after data has loaded from the server AND when this is
        // a real club tenant (not the DR AI FUTBOL platform host — marketing pages handle
        // their own SEO via react-helmet-async to avoid race conditions overwriting <title>).
        if (loaded && branding.name && !branding.is_platform) document.title = branding.name;
        if (branding.favicon_url) {
            let link = document.querySelector("link[rel='icon']");
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = branding.favicon_url;
        }
    }, [branding, loaded]);

    return (
        <BrandingContext.Provider value={{ branding, loaded, refresh: () => api.get("/public/club/branding").then((r) => setBranding({ ...DEFAULT_BRANDING, ...r.data })) }}>
            {children}
        </BrandingContext.Provider>
    );
};

export const useBranding = () => {
    const ctx = useContext(BrandingContext);
    if (!ctx) return { branding: DEFAULT_BRANDING, loaded: false };
    return ctx;
};

// Helper: lighten/darken a hex color
function shadeColor(hex, percent) {
    try {
        const num = parseInt(hex.replace("#", ""), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, Math.min(255, (num >> 16) + amt));
        const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
        const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
        return "#" + ((R << 16) | (G << 8) | B).toString(16).padStart(6, "0");
    } catch {
        return hex;
    }
}
